import { EnvironmentProviders, inject, provideAppInitializer } from "@angular/core";
import {
    AllCommunityModule,
    ColumnState,
    GridApi,
    GridOptions,
    ModuleRegistry,
    provideGlobalGridOptions,
    setupAgTestIds,
} from "ag-grid-community";
import { AppConfig, Environment } from "app/common/app-config";
import { first } from "rxjs";
import { desktopMinWidth } from "../responsive.service";

// Registered at the app root (rather than by the grid wrapper) so it is in place before any grid
// renders, wherever that grid lives.
export function provideAgGrid(): EnvironmentProviders {
    return provideAppInitializer(() => {
        // Loaded with <link>s rather than through angular.json styles to keep them out of the
        // render-blocking global stylesheet, but started here so the request doesn't race a
        // lazy-loaded grid's first render.
        loadCss("ag-grid-community/styles/ag-grid.min.css");
        loadCss("ag-grid-community/styles/ag-theme-material.min.css");

        ModuleRegistry.registerModules([AllCommunityModule]);
        provideGlobalGridOptions(globalGridOptions, "deep");

        inject(AppConfig)
            .environment$.pipe(first())
            .subscribe((environment) => {
                if (environment == Environment.Development || environment == Environment.E2e) {
                    setupAgTestIds();
                }
            });
    });
}

function loadCss(url: string) {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = url;
    document.head.appendChild(link);
}

// App-wide grid defaults. Anything expressible as a grid option belongs here rather than in a
// directive or wrapper component, so it applies to every grid without being wired up per usage.
// Merged deeply so a grid supplying its own defaultColDef still gets the defaults below.
const globalGridOptions: GridOptions = {
    theme: "legacy",
    accentedSort: true,
    animateRows: true,
    suppressMovableColumns: true,
    enableCellTextSelection: true,
    defaultColDef: {
        // Match the quick filter against what the user sees rather than the raw value
        getQuickFilterText: (params) =>
            params.api.getCellValue({
                rowNode: params.node,
                colKey: params.column,
                useFormatter: true,
            }) ?? "",
    },
    onGridReady: ({ api }) => unpinColumnsOnMobile(api),
};

// Pinned columns take up too much of a narrow screen, so unpin them on mobile and restore them
// if the screen widens again. Columns can arrive after the grid is ready (e.g. async columnDefs),
// so unpin those too as they load. Pin states captured on reload come from the column defs, so a
// pin applied only through the api would be lost — fine while the UI offers no manual pinning.
function unpinColumnsOnMobile(api: GridApi) {
    const desktopQuery = window.matchMedia(`(min-width: ${desktopMinWidth}px)`);
    let originalPinStates: ColumnState[] = [];

    const unpin = () => {
        originalPinStates = api.getColumnState().map((c) => ({ colId: c.colId, pinned: c.pinned }));
        api.applyColumnState({ defaultState: { pinned: null } });
    };

    const applyPinning = () => {
        if (desktopQuery.matches) {
            api.applyColumnState({ state: originalPinStates });
            originalPinStates = [];
        } else {
            unpin();
        }
    };

    applyPinning();
    desktopQuery.addEventListener("change", applyPinning);
    api.addEventListener("newColumnsLoaded", () => {
        if (!desktopQuery.matches) {
            unpin();
        }
    });
    api.addEventListener("gridPreDestroyed", () =>
        desktopQuery.removeEventListener("change", applyPinning),
    );
}
